import { BuilderComponent, BuilderContent, useIsPreviewing } from '@builder.io/react';
import { builder } from '@builder.io/sdk';

import StaticComponentGenerator from 'utils/ComponentGenerator';
import SectionModel from 'utils/builder/SectionModel';

import type { BuilderContent as BuilderContentProps, BuilderElement } from '@builder.io/sdk';

interface BuilderPageProps {
  content?: BuilderContentProps;
  model?: string;
  data?: Record<string, string>;
}

interface RenderContentProps {
  content?: BuilderContentProps;
  model: 'header-nav' | 'footers' | 'announcement-bar';
}

interface RenderContentPropsLanding {
  content?: BuilderContentProps;
  model: 'header-landing' | 'footers-landing';
}

builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY || '');

const getBlocks = (content?: BuilderContentProps): BuilderElement[] | undefined => {
  const blocks = (content?.data as { blocks?: BuilderElement[] } | undefined)?.blocks;

  return Array.isArray(blocks) ? blocks : undefined;
};

export const RenderBuilderComponents = ({ content, model = 'page', data, ...props }: BuilderPageProps) => {
  const isPreviewing = useIsPreviewing();

  if (isPreviewing) {
    return (
      <BuilderComponent
        content={content}
        model={model}
        options={{
          cachebust: true,
          noTraverse: false,
          includeRefs: true,
        }}
        data={data}
        {...props}
      />
    );
  }

  const blocks = getBlocks(content);
  if (!blocks) {
    return null;
  }

  // BuilderComponent fires impression/click tracking on mount. noTraverse + children
  // keeps SSR of rich content and avoids the cdn.builder.io refetch on hydration.
  return (
    <BuilderComponent
      content={content}
      model={model}
      options={{
        cachebust: false,
        noTraverse: true,
        includeRefs: false,
      }}
      data={data}
      {...props}
    >
      <StaticComponentGenerator components={blocks} data={data} />
    </BuilderComponent>
  );
};

export const RenderBuilderComponentsLanding = ({
  content,
  model = 'landing-page',
  data,
  ...props
}: BuilderPageProps) => {
  const isPreviewing = useIsPreviewing();

  if (isPreviewing) {
    return (
      <BuilderComponent
        content={content}
        model={model}
        options={{
          cachebust: true,
          noTraverse: false,
          includeRefs: true,
        }}
        data={data}
        {...props}
      />
    );
  }

  const blocks = getBlocks(content);
  if (!blocks) {
    return null;
  }

  return (
    <BuilderComponent
      content={content}
      model={model}
      options={{
        cachebust: false,
        noTraverse: true,
        includeRefs: false,
      }}
      data={data}
      {...props}
    >
      <StaticComponentGenerator components={blocks} data={data} />
    </BuilderComponent>
  );
};

export const RenderBuilderContent = ({ content, model }: RenderContentProps) => {
  const isPreviewing = useIsPreviewing();

  if ((content || isPreviewing) && model) {
    return (
      <BuilderContent
        model={model}
        content={content}
        options={{ cachebust: isPreviewing, noTraverse: false, includeRefs: false }}
      >
        {(data, loading) => !loading && <SectionModel data={data} model={model} />}
      </BuilderContent>
    );
  }
};

export const RenderBuilderContentLanding = ({ content, model }: RenderContentPropsLanding) => {
  const isPreviewing = useIsPreviewing();

  if ((content || isPreviewing) && model) {
    return (
      <BuilderContent
        model={model}
        content={content}
        options={{ cachebust: isPreviewing, noTraverse: false, includeRefs: false }}
      >
        {(data, loading) => !loading && <SectionModel data={data} model={model} />}
      </BuilderContent>
    );
  }
};
